import React from "react";
import { Link } from "react-router-dom";
import promoBanner from "../../assets/join-as-dsa-banner.webp";
import {
  TrendingUp,
  Zap,
  Clock,
  Headphones,
  ArrowRight,
  Sparkles,
} from "lucide-react";

function DSAHero() {
  return (
    <section className="relative overflow-hidden bg-[#f7faf8] pt-6 pb-14 sm:pt-8 sm:pb-18 lg:pt-10 lg:pb-22">
      {/* Soft Ambient Background Glows */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_12%_35%,rgba(46,229,162,0.12),transparent_40%),radial-gradient(circle_at_88%_45%,rgba(0,135,81,0.08),transparent_40%)]"
      />

      <div className="section-container relative z-10">
        
        {/* ================= 1. BREADCRUMB ================= */}
        <nav className="flex items-center gap-2 font-jakarta text-[13px] text-[#6b7280] mb-6 sm:mb-8">
          <Link
            to="/"
            className="hover:text-[var(--hero-green)] transition-colors"
          >
            Home
          </Link>
          <span className="text-[#9ca3af]">›</span>
          <span className="font-semibold text-[#111827]">
            DSA Partner Program
          </span>
        </nav>

        {/* ================= 2. MAIN HERO 2-COLUMN GRID ================= */}
        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12 lg:gap-10 xl:gap-14">
          
          {/* LEFT COLUMN: Headline, Value Proposition, Feature Badges, CTA & Social Proof */}
          <div className="lg:col-span-6 xl:col-span-6">
            
            {/* Tag Badge */}
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--hero-green-light)] px-3.5 py-1 font-jakarta text-[12px] font-bold text-[var(--hero-green)] border border-[var(--hero-green-border)] shadow-xs mb-4">
              <Sparkles size={13} className="text-[var(--hero-green)]" />
              <span>FINVERSE PARTNER NETWORK</span>
            </span>

            {/* Main Headline */}
            <h1 className="font-syne text-[28px] sm:text-[38px] md:text-[46px] lg:text-[54px] xl:text-[60px] font-bold leading-[1.15] tracking-[-0.5px] sm:tracking-[-1.5px] text-[#111827] text-balance">
              Partner with <span className="text-[#111827]">Finverse</span>{" "}
              <span className="text-[var(--hero-green)]">
                Grow. Earn. Succeed.
              </span>
            </h1>

            {/* Sub-headline */}
            <p className="mt-4 sm:mt-5 font-jakarta text-[15px] sm:text-[16.5px] lg:text-[17px] leading-[1.65] text-[#556369]">
              Join our DSA partner network and unlock a world of opportunities.
              Earn attractive commissions by helping your customers access the
              right financial solutions with guaranteed T+7 payouts.
            </p>

            {/* 4 Feature Badges in 2x2 Grid */}
            <div className="mt-6 sm:mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3.5 sm:gap-4">
              
              {/* Feature 1 */}
              <div className="flex items-center gap-2.5 rounded-xl bg-white p-2.5 sm:p-3 border border-[#dce8e1] shadow-2xs">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[var(--hero-green-light)] text-[var(--hero-green)] border border-[var(--hero-green-border)]">
                  <TrendingUp size={18} />
                </div>
                <span className="font-jakarta text-[13px] sm:text-[13.5px] font-bold text-[#111827]">
                  High Earning Potential
                </span>
              </div>

              {/* Feature 2 */}
              <div className="flex items-center gap-2.5 rounded-xl bg-white p-2.5 sm:p-3 border border-[#dce8e1] shadow-2xs">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[var(--hero-green-light)] text-[var(--hero-green)] border border-[var(--hero-green-border)]">
                  <Zap size={18} />
                </div>
                <span className="font-jakarta text-[13px] sm:text-[13.5px] font-bold text-[#111827]">
                  Quick & Easy Onboarding
                </span>
              </div>

              {/* Feature 3 */}
              <div className="flex items-center gap-2.5 rounded-xl bg-white p-2.5 sm:p-3 border border-[#dce8e1] shadow-2xs">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[var(--hero-green-light)] text-[var(--hero-green)] border border-[var(--hero-green-border)]">
                  <Clock size={18} />
                </div>
                <span className="font-jakarta text-[13px] sm:text-[13.5px] font-bold text-[#111827]">
                  Real-time Tracking
                </span>
              </div>

              {/* Feature 4 */}
              <div className="flex items-center gap-2.5 rounded-xl bg-white p-2.5 sm:p-3 border border-[#dce8e1] shadow-2xs">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[var(--hero-green-light)] text-[var(--hero-green)] border border-[var(--hero-green-border)]">
                  <Headphones size={18} />
                </div>
                <span className="font-jakarta text-[13px] sm:text-[13.5px] font-bold text-[#111827]">
                  Dedicated Support
                </span>
              </div>

            </div>

            {/* CTA Button: Directly jumps to #join-as-dsa */}
            <div className="mt-7 sm:mt-8">
              <a
                href="#join-as-dsa"
                className="inline-flex items-center gap-2.5 rounded-full bg-[var(--hero-green)] px-8 py-3.5 font-jakarta text-[15px] font-bold text-white shadow-md transition-all duration-200 hover:bg-[var(--hero-green-hover)] hover:shadow-lg hover:-translate-y-0.5 cursor-pointer outline-none focus:outline-none"
              >
                <span>Become a DSA Partner</span>
                <ArrowRight size={17} strokeWidth={2.4} />
              </a>
            </div>

          </div>

          {/* RIGHT COLUMN: Promo CTA Image Banner Showcase */}
          <div className="lg:col-span-6 xl:col-span-6 flex items-center justify-center">
            <div className="group relative w-full overflow-hidden rounded-[26px]           ">
              
              {/* Banner Image */}
              <img
                src={promoBanner}
                alt="Finverse Loan Approval & DSA Partner Platform"
                className="relative z-10 w-full rounded-[20px] sm:rounded-[24px] object-cover transition-transform duration-300 group-hover:scale-[1.01]"
                loading="eager"
              />
            </div>
          </div>

        </div>

      </div>
    </section>
  );
}

export default DSAHero;
